import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import ReactQuill from "react-quill";
import "./style.css";

const BaseEditor = forwardRef((props, ref) => {
  const {
    value,
    onChange,
    className,
    readOnly,
    showToolbar = true,
    onKeyDown,
    placeholder = "",
    editorClassName,
    // Lets a caller style or, as the chat composer does, dissolve this wrapper.
    wrapperClassName = "",
  } = props;

  const quillRef = useRef(null);

  const toolbarOptions = [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
  ];

  const modules = {
    toolbar: showToolbar && !readOnly ? toolbarOptions : false,
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
    "align",
  ];

  useImperativeHandle(ref, () => ({
    focus: () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      setTimeout(() => {
        editor.focus();
        const length = editor.getLength();
        editor.setSelection(length, 0);
      }, 0);
    },
    blur: () => {
      const editor = quillRef.current?.getEditor();
      editor?.blur();
    },
    getHTML: () => {
      return quillRef.current?.getEditor()?.root?.innerHTML;
    },
  }));

  // Quill's extra arguments are passed on (after the HTML, so existing callers are
  // unaffected) - `source` tells a user's edit apart from Quill tidying a loaded value.
  const handleChange = (body, delta, source, editor) => {
    onChange(body, delta, source, editor);
  };

  const handleDoubleClick = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const contentLength = editor.getLength();
      editor.setSelection(0, contentLength);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection();
      if (range && editor.getFormat(range.index).list) return;
      if (onKeyDown) onKeyDown(e);
    }
  };

  useEffect(() => {
    if (editorClassName !== "ql-editor") return;

    const editor = document.querySelector(`.${editorClassName}`);
    if (editor) {
      editor.setAttribute(
        "style",
        "min-height: 250px !important; max-height: 350px !important; overflow-y: auto !important; box-sizing: border-box; cursor: text;",
      );
    }
  }, [editorClassName]);

  return (
    <div className={wrapperClassName} onDoubleClick={handleDoubleClick}>
      <ReactQuill
        key={showToolbar ? "withToolbar" : "noToolbar"}
        ref={quillRef}
        readOnly={readOnly}
        className={className}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        editorClassName={editorClassName}
      />
    </div>
  );
});

export default BaseEditor;
